import { useEffect, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';

/**
 * Side-panel state while the AI reads the document.
 *
 * Panel-sized version of the contract-checker's `LoadingOverlay`: staged progress against a
 * synthetic clock, since the agent streams no real progress events (it buffers and resolves
 * once). Deliberately NOT a full-screen overlay — the document canvas stays visible and
 * scrollable so the wait is spent reading the contract rather than watching a spinner, and the
 * user can still mark a field by hand.
 *
 * The bar never reaches 100%: it crawls toward 95 and stops. The real call landing is what
 * unmounts this component, so a bar sitting at 100 while nothing happens is never shown.
 */

interface Stage {
  title: string;
  /** Cumulative percent at the END of this stage. */
  end: number;
  /** Seconds the bar spends crawling through it. */
  duration: number;
}

const STAGES: Stage[] = [
  { title: 'Reading the document', end: 20, duration: 3 },
  { title: 'Identifying the parties', end: 50, duration: 8 },
  { title: 'Finding dates, amounts and details', end: 78, duration: 10 },
  { title: 'Grouping repeated values', end: 95, duration: 9 },
];

const TOTAL_DURATION = STAGES.reduce((acc, s) => acc + s.duration, 0);

const TIPS = [
  'Anything the AI suggests can be renamed, retyped or removed before you save.',
  'Repeated values are linked automatically — fill the name once and it appears everywhere.',
  'Signature lines become anchors, so the signing box lands in the right place.',
  'Nothing is saved until you choose Save and continue.',
];

export function SuggestionLoading() {
  const [elapsed, setElapsed] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const startedAt = performance.now();
    const interval = setInterval(() => setElapsed((performance.now() - startedAt) / 1000), 200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 5000);
    return () => clearInterval(interval);
  }, []);

  // Walk the stages in real time, then creep from 95 toward 99 while the call is still out.
  let progress = 95;
  let activeIndex = STAGES.length - 1;
  let consumed = 0;
  for (let i = 0; i < STAGES.length; i++) {
    const stage = STAGES[i];
    const within = elapsed - consumed;
    if (within < stage.duration) {
      const prevEnd = i === 0 ? 0 : STAGES[i - 1].end;
      progress = prevEnd + (stage.end - prevEnd) * Math.max(0, Math.min(1, within / stage.duration));
      activeIndex = i;
      break;
    }
    consumed += stage.duration;
  }
  if (elapsed > TOTAL_DURATION) {
    progress = Math.min(99, 95 + (elapsed - TOTAL_DURATION) * 0.2);
  }

  return (
    <div
      className="flex h-full flex-col gap-5 overflow-y-auto p-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-selise-blue/20 bg-selise-blue/10">
          <Sparkles className="h-5 w-5 animate-pulse text-selise-blue" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-[hsl(var(--fg))]">
            Finding fields that make your workflow easier
          </h2>
          <p className="mt-0.5 text-xs text-globe-grey">
            This usually takes 15–40 seconds. You can keep reading the document while it works.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-[hsl(var(--fg))] tabular-nums">
            {Math.round(progress)}%
          </span>
          <span className="text-globe-grey">
            Step {activeIndex + 1} of {STAGES.length}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-selise-blue/10">
          <div
            className="h-full rounded-full bg-selise-blue transition-[width] duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ul className="space-y-2.5">
        {STAGES.map((stage, index) => {
          const done = index < activeIndex;
          const current = index === activeIndex;
          return (
            <li
              key={stage.title}
              className={`flex items-center gap-2.5 text-xs transition-colors ${
                done
                  ? 'text-selise-blue'
                  : current
                    ? 'font-medium text-[hsl(var(--fg))]'
                    : 'text-globe-grey'
              }`}
            >
              {done ? (
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-selise-blue text-white">
                  <Check className="h-2.5 w-2.5" />
                </span>
              ) : current ? (
                <span className="h-4 w-4 shrink-0 animate-pulse rounded-full border-2 border-selise-blue bg-selise-blue/10" />
              ) : (
                <span className="h-4 w-4 shrink-0 rounded-full border-2 border-border" />
              )}
              {stage.title}
            </li>
          );
        })}
      </ul>

      <div className="mt-auto rounded-lg border border-border bg-card p-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-selise-blue">
          Did you know?
        </p>
        <p className="text-xs leading-relaxed text-globe-grey">{TIPS[tipIndex]}</p>
      </div>
    </div>
  );
}

export default SuggestionLoading;
